'use client'

import React, { useEffect, useMemo, useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

export function RaiseCeilingDialog({
  open,
  onCancel,
  onConfirm,
  currentCeilingCents,
  requestedCents,
  platformName,
}: {
  open: boolean
  onCancel: () => void
  onConfirm: (newCeilingCents: number) => Promise<void>
  currentCeilingCents: number
  requestedCents: number
  platformName: string
}) {
  const suggestedCents = useMemo(() => niceCeilingCents(requestedCents), [requestedCents])
  const [draftDollars, setDraftDollars] = useState('')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (open) setDraftDollars(String(Math.round(suggestedCents / 100)))
  }, [open, suggestedCents])

  const parsedCents = Math.round(Number(draftDollars) * 100)
  const valid = Number.isFinite(parsedCents) && parsedCents >= requestedCents

  const confirm = async () => {
    if (!valid || saving) return
    setSaving(true)
    try {
      await onConfirm(parsedCents)
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => { if (!nextOpen) onCancel() }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Raise safety ceiling?</DialogTitle>
          <DialogDescription>
            You&apos;re setting {platformName} to {formatCents(requestedCents)}. Your safety
            ceiling is {formatCents(currentCeilingCents)}.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 text-sm text-atrium-muted">
          <p>
            The ceiling caps what Atrium can PATCH to Meta without your acknowledgment.
            It&apos;s protection against bugs, not a spend limit.
          </p>
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-atrium-black">
              Raise the ceiling to
            </span>
            <div className="flex items-center rounded-md border border-atrium-border bg-white px-3 py-2">
              <span className="mr-1 text-atrium-muted">$</span>
              <input
                type="number"
                min={Math.ceil(requestedCents / 100)}
                value={draftDollars}
                onChange={(e) => setDraftDollars(e.target.value)}
                className="w-full bg-transparent font-mono text-sm text-atrium-black outline-none"
              />
            </div>
          </label>
          {!valid && (
            <p className="text-xs text-red-600">
              Enter at least {formatCents(requestedCents)} to apply this budget.
            </p>
          )}
        </div>
        <DialogFooter>
          <button
            type="button"
            onClick={onCancel}
            className="rounded-lg border border-atrium-border bg-white px-4 py-2 text-sm text-atrium-black transition-colors hover:bg-atrium-content"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={confirm}
            disabled={!valid || saving}
            className="rounded-lg bg-atrium-black px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-atrium-black/90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {saving ? 'Applying...' : `Raise ceiling to ${formatCents(parsedCents || suggestedCents)} and apply`}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export function niceCeilingCents(requestedCents: number): number {
  const requestedDollars = Math.ceil(requestedCents / 100)
  let increment = 50_000
  if (requestedDollars < 1_000) increment = 100
  else if (requestedDollars < 10_000) increment = 1_000
  else if (requestedDollars < 100_000) increment = 10_000
  return Math.ceil(requestedDollars / increment) * increment * 100
}

function formatCents(cents: number): string {
  return `$${(cents / 100).toLocaleString('en-US', { maximumFractionDigits: 0 })}`
}
